<template>
  <v-container id="draft-orders" class="align-start" fluid>
    <i-overlay
      v-if="fetching && $vuetifyDisplay.smAndDown"
      text="Loading draft purchase orders..."
    />

    <v-row v-else id="content" no-gutters>
      <i-local-filter
        class="mb-6"
        :filterPlaceholder="`Search Orders by PO Number or ${
          this.isILR ? 'Customer' : 'Retailer'
        }`"
        @filter="localFilter"
      />

      <v-col cols="12">
        <template v-if="!fetching && filteredDraftOrders.length === 0">
          <i-empty-results
            buttonText="Dashboard"
            buttonRouteName="dashboard"
            :title="emptyFilteredResultsText"
          />
        </template>

        <template v-else>
          <v-row no-gutters>
            <v-col cols="12">
              <template v-if="$vuetifyDisplay.smAndDown">
                <div
                  class="mt-2 pa-0"
                  v-for="item in filteredDraftOrders"
                  :key="item.id"
                >
                  <v-card class="rounded-2 pa-4 pa-md-6 mb-6">
                    <v-row no-gutters>
                      <v-col cols="6" class="py-1">
                        <span
                          ><strong>{{ headers[0].text }}</strong></span
                        >
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span
                          ><strong>{{
                            $filters.text(item.supplierOrderNumber)
                          }}</strong></span
                        >
                      </v-col>
                    </v-row>

                    <v-divider class="my-2"></v-divider>

                    <v-row no-gutters>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[1].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.formatDateTime12h(item.createdAt)
                        }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>
                          {{ headers[2].text }}
                          <v-icon
                            class="i-tooltip-color pl-1 pb-1"
                            small
                            v-tooltip="'Includes fees'"
                          >
                            mdi-help-circle-outline
                          </v-icon>
                        </span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.currency(displayOrderTotal(item.summary))
                        }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[3].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <i-payment-type
                          v-if="item.paymentType"
                          :paymentType="item.paymentType"
                          :textOnly="true"
                        />
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[4].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{ $filters.text(item.store.name) }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[5].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.text(item.deliveryAddress.postCode)
                        }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[6].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.text(item.deliveryAddress.state)
                        }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[7].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span class="p-response-date">{{
                          $filters.formatDateTime12h(
                            item.expectedFinalConfirmationDate
                          )
                        }}</span>
                      </v-col>
                    </v-row>

                    <v-divider class="mt-2 mb-4"></v-divider>
                    <template v-if="isServiceOrder(item)">
                      <v-row no-gutters align="center">
                        <v-col cols="6" class="py-1">
                          <span>ORDER TYPE</span>
                        </v-col>

                        <v-col cols="6" class="py-1 pl-4">
                          <v-chip color="secondary" small> SERVICE </v-chip>
                        </v-col>
                      </v-row>
                    </template>

                    <v-btn
                      rounded
                      class="i-btn-block-orange g-full-width"
                      :to="{
                        name: 'purchase-order-details',
                        params: { id: item.id }
                      }"
                    >
                      Confirm Stock Availability
                    </v-btn>
                    <br />
                    <div class="text-center pt-2">
                      <v-btn
                        class="g-full-width i-btn-block-primary mt-1"
                        :disabled="!item.draftPdfAvailable"
                        @click="fetchPDF(item.id)"
                      >
                        <span>Export PDF</span>
                        <v-icon color="white" class="ml-1"
                          >mdi-file-pdf-box</v-icon
                        >
                      </v-btn>
                    </div>
                  </v-card>
                </div>
              </template>

              <template v-else>
                <v-data-table
                  class="pt-2 elevation-1 i-data-table"
                  :headers="headers"
                  :items="filteredDraftOrders"
                  :items-per-page="-1"
                  :loading="fetching"
                  :no-data-text="emptyFilteredResultsText"
                  :no-results-text="emptyFilteredResultsText"
                  sort-by="createdAt"
                  :sort-desc="false"
                >
                  <template
                    v-slot:[`header.summary.remittanceTotal.totalIncludingTax`]="{
                      header
                    }"
                  >
                    {{ header.text }}
                    <v-icon
                      class="i-tooltip-color pl-1 pb-1"
                      small
                      v-tooltip="'Includes fees'"
                    >
                      mdi-help-circle-outline
                    </v-icon>
                  </template>

                  <template v-slot:[`item.supplierOrderNumber`]="{ item }">
                    <span>{{ $filters.text(item.supplierOrderNumber) }}</span>
                  </template>

                  <template v-slot:[`item.createdAt`]="{ item }">
                    <span>{{
                      $filters.formatDateTime12h(item.createdAt)
                    }}</span>
                  </template>

                  <template
                    v-slot:[`item.summary.remittanceTotal.totalIncludingTax`]="{
                      item
                    }"
                  >
                    <span>{{
                      $filters.currency(displayOrderTotal(item.summary))
                    }}</span>
                  </template>

                  <template v-slot:[`item.payment-type`]="{ item }">
                    <i-payment-type
                      v-if="item.paymentType"
                      :paymentType="item.paymentType"
                      :textOnly="true"
                    />
                  </template>

                  <template v-slot:[`item.store.name`]="{ item }">
                    <span>{{ $filters.text(item.store.name) }}</span>
                  </template>

                  <template v-slot:[`item.deliveryAddress.postCode`]="{ item }">
                    <span>{{
                      $filters.text(item.deliveryAddress.postCode)
                    }}</span>
                  </template>

                  <template v-slot:[`item.deliveryAddress.state`]="{ item }">
                    <span>{{ $filters.text(item.deliveryAddress.state) }}</span>
                  </template>

                  <template
                    v-slot:[`item.expectedFinalConfirmationDate`]="{ item }"
                  >
                    <span
                      class="p-response-date"
                      :class="{
                        'p-response-date--alert':
                          confirmationDateExceedsThreshold(
                            item.expectedFinalConfirmationDate
                          )
                      }"
                      >{{
                        $filters.formatDateTime12h(
                          item.expectedFinalConfirmationDate
                        )
                      }}</span
                    >
                  </template>

                  <template v-slot:[`item.action`]="{ item }">
                    <v-btn
                      rounded
                      class="i-btn-block-orange g-full-width"
                      :to="{
                        name: 'purchase-order-details',
                        params: { id: item.id }
                      }"
                    >
                      Action
                    </v-btn>
                  </template>
                  <template v-slot:[`item.orderType`]="{ item }">
                    <v-chip v-if="isServiceOrder(item)" color="secondary" small>
                      SERVICE
                    </v-chip>
                  </template>
                  <template v-slot:[`item.exportPdf`]="{ item }">
                    <v-icon
                      class="i-btn-pdf"
                      :disabled="!item.draftPdfAvailable"
                      @click="fetchPDF(item.id)"
                      >mdi-file-pdf-box</v-icon
                    >
                  </template>
                </v-data-table>
              </template>
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { fetchOrderPDF } from '@/services/supplierOrders'
import iOverlay from '@/components/Overlay'
import iLocalFilter from '@/components/LocalFilter'
import { openFile } from '@/utils/common'
import moment from 'moment'
import iEmptyResults from '@/components/EmptyResults'
import iPaymentType from '@/components/PaymentType'
import { pickSupplierCostTotal } from '@/utils/supplierAmountDisplay'

export default {
  compatConfig: {
    WATCH_ARRAY: false
  },

  components: {
    iPaymentType,
    iEmptyResults,
    iOverlay,
    iLocalFilter
  },

  props: {
    fetching: {
      default: false,
      type: Boolean
    },

    orders: {
      default: () => [],
      type: Array
    }
  },

  data() {
    return {
      emptyFilteredResultsText: 'No DRAFT Purchase Orders found',
      draftOrders: [],
      filterText: ''
    }
  },

  computed: {
    ...mapGetters('tenant', [
      'isILR',
      'tenantCountryRegion',
      'usesAustralianTaxLabels'
    ]),

    headers() {
      return [
        {
          text: 'PO NUMBER',
          value: 'supplierOrderNumber',
          align: 'start',
          width: '7%'
        },
        {
          text: 'ORDER DATE & TIME',
          value: 'createdAt',
          align: 'center',
          width: '13%'
        },
        {
          text: this.usesAustralianTaxLabels
            ? 'PO TOTAL (INC TAX)'
            : 'PO TOTAL',
          value: 'summary.remittanceTotal.totalIncludingTax',
          align: 'center',
          width: '12%'
        },
        {
          text: 'PAYMENT TYPE',
          value: 'payment-type',
          align: 'center',
          sortable: false,
          width: '8%'
        },
        {
          text: `${this.isILR ? 'CUSTOMER' : 'RETAILER'}`,
          value: 'store.name',
          width: '18%'
        },
        {
          text: 'POSTCODE',
          value: 'deliveryAddress.postCode',
          align: 'center',
          width: '8%'
        },
        {
          text: 'STATE',
          value: 'deliveryAddress.state',
          align: 'center',
          width: '8%'
        },
        {
          text: 'REQUIRED RESPONSE DATE & TIME',
          value: 'expectedFinalConfirmationDate',
          align: 'center',
          width: '14%'
        },
        {
          text: 'STOCK AVAILABILITY',
          value: 'action',
          align: 'center',
          sortable: false,
          width: 175
        },
        {
          text: 'ORDER TYPE',
          value: 'orderType',
          align: 'center',
          sortable: false,
          width: '8%'
        },
        {
          text: 'EXPORT',
          value: 'exportPdf',
          align: 'center',
          sortable: false,
          width: '8%'
        }
      ]
    },

    filteredDraftOrders() {
      return this.draftOrders.filter((order) => {
        return (
          order.supplierOrderNumber
            .toLowerCase()
            .includes(this.filterText.toLowerCase()) ||
          order.store.name.toLowerCase().includes(this.filterText.toLowerCase())
        )
      })
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    displayOrderTotal(summary) {
      return pickSupplierCostTotal({
        tenantCountryRegion: this.tenantCountryRegion,
        cost: summary?.remittanceTotal
      })
    },

    localFilter(filterText) {
      if (typeof filterText !== 'string') filterText = ''
      this.filterText = filterText
    },

    fetchPDF(orderId) {
      fetchOrderPDF(orderId)
        .then((response) => {
          const file = new Blob([response], { type: 'application/pdf' })
          const fileURL = URL.createObjectURL(file)
          openFile(fileURL)
        })
        .catch((err) => {
          err.response.status === 404
            ? this.addNotification({
                color: 'error',
                text: `Invoice ${orderId} not found`
              })
            : this.addNotification({ color: 'error', defaultError: true })
        })
    },

    confirmationDateExceedsThreshold(confirmationDate) {
      // Determine if the confirmation date is more than 24 hours old
      const threshold = moment().subtract(24, 'HOURS')
      return moment(confirmationDate).isBefore(threshold)
    },

    isServiceOrder(order) {
      return order?.supplierOrderItems?.some(
        (item) => item.productType === 'SERVICE'
      )
    }
  },

  mounted() {
    this.draftOrders = _.cloneDeep(this.orders)
  },

  watch: {
    orders: {
      deep: true,
      handler() {
        // need watcher as orders is an async prop
        this.draftOrders = _.cloneDeep(this.orders)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#draft-orders {
  #content {
    .p-response-date {
      color: $secondary-color;
      font-weight: bold;
    }
    .p-response-date--alert {
      color: $error-color;
    }
  }
}
</style>
